/**
 * PostSubmitPanel — the "morph in place" content shown in the feedback overlay's
 * right column after an issue is submitted.
 *
 * Instead of the overlay vanishing on submit, it hands off to this panel, which
 * walks the reporter through:
 *   analyzing  → "AI is reading your report…" (watches triage via useIssueTriageWatch)
 *   questions  → the AI's clarifying questions, answerable right here
 *   done       → a confirmation (admins additionally get "View full ticket →")
 *
 * Answers are recorded as a public comment by the real person (the admin while
 * impersonating). The database trigger then clears the questions and queues the
 * agent's re-read for the lodger whatever their role, so the panel no longer
 * updates the row or calls triage-issues itself.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    LinearProgress,
    CircularProgress,
    Fade,
    Slide,
    Zoom,
    Divider,
    Alert,
    InputAdornment,
} from '@mui/material';
import {
    CheckCircleRounded as CheckCircleIcon,
    AutoAwesome as AutoAwesomeIcon,
    Send as SendIcon,
    OpenInNew as OpenInNewIcon,
    CheckRounded as CheckRoundedIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useIssueTriageWatch } from './useIssueTriageWatch';
import { formatClarifyingAnswersComment } from './clarifyingAnswers';
import { realUserId } from './lodger';

const ACCENT = '#e65100'; // matches the detail view's "Action Required" card

const ANALYZING_LINES = [
    'Reading your description…',
    'Looking at your screenshot…',
    'Checking for duplicate reports…',
    'Working out if we need to ask you anything…',
];

interface PostSubmitPanelProps {
    /** The freshly-created issue id, or null when it could not be read back. */
    issueId: string | null;
    isAdmin: boolean;
    reducedMotion: boolean;
    /** Close the whole overlay ("Done" / "Skip"). */
    onClose: () => void;
    /** Navigate to the full admin ticket (admins only). */
    onViewTicket: (issueId: string) => void;
}

type DoneReason = 'answered' | 'skipped' | 'no_questions' | 'analyzing_bg' | 'submitted' | 'duplicate';

export const PostSubmitPanel: React.FC<PostSubmitPanelProps> = ({
    issueId,
    isAdmin,
    reducedMotion,
    onClose,
    onViewTicket,
}) => {
    const { user } = useAuth();
    const watch = useIssueTriageWatch(issueId);

    const [answers, setAnswers] = useState<Record<number, string>>({});
    const [submitting, setSubmitting] = useState(false);
    const [answered, setAnswered] = useState(false);
    const [skipped, setSkipped] = useState(false);
    const [answerError, setAnswerError] = useState<string | null>(null);
    const [lineIdx, setLineIdx] = useState(0);

    const questions = watch.questions;

    const phase: 'analyzing' | 'questions' | 'done' = useMemo(() => {
        if (!issueId) return 'done';
        if (answered || skipped) return 'done';
        if (!watch.ready) return 'analyzing';
        return questions.length > 0 ? 'questions' : 'done';
    }, [issueId, answered, skipped, watch.ready, questions.length]);

    const doneReason: DoneReason = useMemo(() => {
        if (!issueId) return 'submitted';
        if (answered) return 'answered';
        if (skipped) return 'skipped';
        if (watch.timedOut && !watch.settled) return 'analyzing_bg';
        if (watch.isDuplicate) return 'duplicate';
        return 'no_questions';
    }, [issueId, answered, skipped, watch.timedOut, watch.settled, watch.isDuplicate]);

    // Rotate the analyzing status line.
    useEffect(() => {
        if (phase !== 'analyzing') return;
        const id = setInterval(
            () => setLineIdx((i) => (i + 1) % ANALYZING_LINES.length),
            2200,
        );
        return () => clearInterval(id);
    }, [phase]);

    const hasAnswers =
        questions.length > 0 &&
        questions.some((_, idx) => (answers[idx] ?? '').trim().length > 0);

    const handleSubmitAnswers = async () => {
        if (!issueId || !hasAnswers) return;
        setSubmitting(true);
        setAnswerError(null);
        try {
            const body = formatClarifyingAnswersComment(questions, answers);
            const { error: commentError } = await supabase
                .from('site_issue_comments')
                .insert({
                    issue_id: issueId,
                    author_id: realUserId(user),
                    body,
                    is_internal_only: false,
                });
            if (commentError) throw commentError;

            setAnswered(true);
        } catch (err) {
            console.error('Failed to submit clarifying answers:', err);
            setAnswerError('Sorry — we could not save your answers. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const fadeTimeout = reducedMotion ? 0 : 400;

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 3, overflowY: 'auto' }}>
            <Fade key={phase} in appear timeout={fadeTimeout}>
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    {phase === 'analyzing' && (
                        <AnalyzingView line={ANALYZING_LINES[lineIdx]} reducedMotion={reducedMotion} />
                    )}

                    {phase === 'questions' && (
                        <QuestionsView
                            questions={questions}
                            answers={answers}
                            setAnswers={setAnswers}
                            submitting={submitting}
                            hasAnswers={hasAnswers}
                            answerError={answerError}
                            reducedMotion={reducedMotion}
                            onSubmit={handleSubmitAnswers}
                            onSkip={() => setSkipped(true)}
                        />
                    )}

                    {phase === 'done' && (
                        <DoneView
                            reason={doneReason}
                            confidence={watch.confidence}
                            isAdmin={isAdmin}
                            canViewTicket={!!issueId}
                            reducedMotion={reducedMotion}
                            onClose={onClose}
                            onViewTicket={() => issueId && onViewTicket(issueId)}
                        />
                    )}
                </Box>
            </Fade>
        </Box>
    );
};

// ── Analyzing ───────────────────────────────────────────────────────────────

const AnalyzingView: React.FC<{ line: string; reducedMotion: boolean }> = ({ line, reducedMotion }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, textAlign: 'center', gap: 2, py: 4 }}>
        <Zoom in appear timeout={reducedMotion ? 0 : 400}>
            <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main' }} />
        </Zoom>
        <Typography variant="h6" fontWeight={700}>Issue submitted</Typography>

        <Box
            sx={{
                mt: 1,
                width: '100%',
                maxWidth: 340,
                p: 2.5,
                borderRadius: 2,
                bgcolor: 'action.hover',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <AutoAwesomeIcon
                    fontSize="small"
                    sx={{
                        color: ACCENT,
                        ...(reducedMotion
                            ? {}
                            : { animation: 'tc-pulse 1.6s ease-in-out infinite' }),
                        '@keyframes tc-pulse': {
                            '0%, 100%': { opacity: 0.45, transform: 'scale(0.9)' },
                            '50%': { opacity: 1, transform: 'scale(1.1)' },
                        },
                    }}
                />
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: ACCENT }}>
                    AI is reading your report
                </Typography>
            </Box>

            <LinearProgress
                sx={{
                    height: 6,
                    borderRadius: 3,
                    '& .MuiLinearProgress-bar': { bgcolor: ACCENT },
                    bgcolor: 'rgba(230, 81, 0, 0.15)',
                }}
            />

            <Fade key={line} in appear timeout={reducedMotion ? 0 : 500}>
                <Typography variant="body2" color="text.secondary" sx={{ minHeight: 20 }}>
                    {line}
                </Typography>
            </Fade>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 320 }}>
            This usually takes a few seconds. We may ask a couple of quick questions to help us fix it faster.
        </Typography>
    </Box>
);

// ── Questions ─────────────────────────────────────────────────────────────

const QuestionsView: React.FC<{
    questions: string[];
    answers: Record<number, string>;
    setAnswers: React.Dispatch<React.SetStateAction<Record<number, string>>>;
    submitting: boolean;
    hasAnswers: boolean;
    answerError: string | null;
    reducedMotion: boolean;
    onSubmit: () => void;
    onSkip: () => void;
}> = ({ questions, answers, setAnswers, submitting, hasAnswers, answerError, reducedMotion, onSubmit, onSkip }) => (
    <Slide direction="up" in appear timeout={reducedMotion ? 0 : 450}>
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <Box sx={{ mb: 1 }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: ACCENT }}>
                    ⚠️ A couple of quick questions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Answering now helps us fix this faster — it only takes a moment.
                </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box
                component="form"
                onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
                sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0 }}
            >
                {questions.map((q, idx) => {
                    const filled = (answers[idx] ?? '').trim().length > 0;
                    return (
                        <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2" fontWeight={600}>
                                {idx + 1}. {q}
                            </Typography>
                            <TextField
                                size="small"
                                fullWidth
                                multiline
                                rows={2}
                                autoFocus={idx === 0}
                                placeholder="Type your answer here…"
                                value={answers[idx] ?? ''}
                                onChange={(e) => setAnswers((prev) => ({ ...prev, [idx]: e.target.value }))}
                                disabled={submitting}
                                InputProps={{
                                    endAdornment: filled ? (
                                        <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                            <CheckRoundedIcon fontSize="small" sx={{ color: 'success.main' }} />
                                        </InputAdornment>
                                    ) : undefined,
                                }}
                            />
                        </Box>
                    );
                })}

                {answerError && <Alert severity="error" sx={{ mt: 0.5 }}>{answerError}</Alert>}

                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={!hasAnswers || submitting}
                        endIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                        sx={{ py: 1.1, fontWeight: 700, bgcolor: ACCENT, '&:hover': { bgcolor: '#b53f00' } }}
                    >
                        {submitting ? 'Sending…' : 'Submit answers'}
                    </Button>
                    <Button onClick={onSkip} disabled={submitting} size="small" color="inherit" sx={{ textTransform: 'none', fontWeight: 600 }}>
                        Skip for now
                    </Button>
                </Box>
            </Box>
        </Box>
    </Slide>
);

// ── Done ──────────────────────────────────────────────────────────────────

const DONE_COPY: Record<DoneReason, { title: string; body: string }> = {
    answered: {
        title: 'Thank you!',
        body: 'Your answers are in. Our team now has what they need to get started.',
    },
    skipped: {
        title: 'All set',
        body: 'Your report is with our team. You can always add more detail later.',
    },
    no_questions: {
        title: "You're all set",
        body: 'Our AI had everything it needed — your report is with the team.',
    },
    analyzing_bg: {
        title: 'Report received',
        body: "Our AI is still analyzing this one. If we need anything, we'll follow up.",
    },
    submitted: {
        title: 'Report received',
        body: 'Thanks for the report — our team will take it from here.',
    },
    duplicate: {
        title: 'Known issue',
        body: 'This looks like something we\'re already tracking. Your report has been linked to the existing ticket so the team has your context too.',
    },
};

const DoneView: React.FC<{
    reason: DoneReason;
    confidence: number | null;
    isAdmin: boolean;
    canViewTicket: boolean;
    reducedMotion: boolean;
    onClose: () => void;
    onViewTicket: () => void;
}> = ({ reason, confidence, isAdmin, canViewTicket, reducedMotion, onClose, onViewTicket }) => {
    const copy = DONE_COPY[reason];
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, textAlign: 'center', gap: 2, py: 4 }}>
            <Zoom in appear timeout={reducedMotion ? 0 : 450}>
                <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
            </Zoom>
            <Typography variant="h6" fontWeight={700}>{copy.title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 340 }}>
                {copy.body}
            </Typography>

            {isAdmin && confidence !== null && reason !== 'analyzing_bg' && (
                <Typography variant="caption" color="text.secondary">
                    Triage certainty: <strong>{confidence}%</strong>
                </Typography>
            )}

            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1, width: '100%', maxWidth: 300 }}>
                {isAdmin && canViewTicket && (
                    <Button
                        variant="contained"
                        color="secondary"
                        endIcon={<OpenInNewIcon />}
                        onClick={onViewTicket}
                        sx={{ fontWeight: 700 }}
                    >
                        View full ticket
                    </Button>
                )}
                <Button variant={isAdmin && canViewTicket ? 'text' : 'contained'} onClick={onClose} sx={{ fontWeight: 600 }}>
                    Done
                </Button>
            </Box>
        </Box>
    );
};
